// Format checks shared by the forms. Each returns an error message, or ""
// when the value is fine. Optional fields pass `required: false`: they may be
// left empty, but anything typed into them has to be complete.

const digitsOf = value => String(value || "").replace(/\D/g, "")

// Masked phone fields hold "(619) 555-0123", or "(61 )    -    " while
// half-typed, so count digits rather than characters
export const phoneError = (value, { required = false, requiredMessage, label = "Phone Number" } = {}) => {
    const digits = digitsOf(value)
    if (!digits) return required ? requiredMessage || `${label} is required` : ""
    if (digits.length !== 10) return `Please enter a 10-digit ${label.toLowerCase()}`
    // Area code and exchange start 2-9 in real US numbers (the subscribe
    // Lambda drops anything else from SMS enrollment without telling anyone)
    return digits[0] < "2" || digits[3] < "2" ? `Please enter a valid ${label.toLowerCase()}` : ""
}

// US ZIP or ZIP+4. Only for US-only forms; Contact Us and Privacy take
// international postal codes
export const zipError = (value, { required = false, requiredMessage, label = "ZIP Code" } = {}) => {
    const v = String(value || "").trim()
    if (!v) return required ? requiredMessage || `${label} is required` : ""
    return /^\d{5}(-\d{4})?$/.test(v) ? "" : `Please enter a 5-digit ${label} (or ZIP+4)`
}

// Replaces the "Confirm Email" fields (the form-system review drops them):
// a stricter check than the old /^[^\s@]+@[^\s@]+\.[^\s@]+$/ patterns. No
// spaces, no leading/trailing/double dots, domain labels that don't start or
// end with a hyphen, and a letters-only top-level domain of 2+ characters.
const EMAIL_LOCAL = "[A-Za-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\\.[A-Za-z0-9!#$%&'*+/=?^_`{|}~-]+)*"
const EMAIL_DOMAIN = "(?:[A-Za-z0-9](?:[A-Za-z0-9-]*[A-Za-z0-9])?\\.)+[A-Za-z]{2,}"
const EMAIL_RE = new RegExp(`^${EMAIL_LOCAL}@${EMAIL_DOMAIN}$`)

export const isValidEmail = value => {
    const v = String(value || "")
    return v.length <= 254 && v.indexOf("@") <= 64 && EMAIL_RE.test(v)
}

// Parse a typed phone number into E.164 (+<country><number>), which is what we
// post as `phone`. The country code is optional: a bare number is assumed to be
// US (+1), matching how the field behaved before country codes were accepted.
//
// Returns { e164, isUs } on success, or null when the number can't be a real
// phone number. E.164 caps the whole thing (country code included) at 15 digits;
// nothing shorter than ~8 is a dialable international number.
//
// isUs mirrors the lambda's normalizeUsPhoneForQueueMo: a valid NANP number is
// 10 digits whose area code and exchange don't start with 0 or 1. Only those can
// be enrolled in SMS (WD-40's short code is US-only), so callers gate SMS
// consent on isUs rather than letting the opt-in silently no-op server-side.
export const parsePhone = raw => {
    const trimmed = String(raw || "").trim()
    const digits = trimmed.replace(/\D/g, "")
    if (!digits) return null

    // No explicit "+", so treat it as a US number: 10 digits, or 11 with the
    // country code already typed as a bare leading 1.
    const explicitCountryCode = trimmed.startsWith("+")
    const isNanp = explicitCountryCode
        ? digits.length === 11 && digits.startsWith("1")
        : digits.length === 10 || (digits.length === 11 && digits.startsWith("1"))

    if (isNanp) {
        const tenDigit = digits.length === 11 ? digits.slice(1) : digits
        // Area code and exchange must start 2-9 to be a real NANP number.
        if (tenDigit[0] < "2" || tenDigit[3] < "2") return null
        return { e164: `+1${tenDigit}`, isUs: true }
    }

    // Anything else is only a phone number if it carried a country code,
    // and "+1" is the US/NANP code: one with the wrong digit count is a
    // mistyped US number, not an international one
    if (!explicitCountryCode) return null
    if (digits.startsWith("1")) return null
    if (digits.length < 8 || digits.length > 15) return null
    return { e164: `+${digits}`, isUs: false }
}

// For forms that accept international numbers (free text, no mask): the
// same rules as Sample Request's phone field
export const INTL_PHONE_HELP =
    "US numbers don't need a country code. Outside the US, start with your country code (e.g. +44)."

export const intlPhoneError = (value, { required = false, requiredMessage, label = "Phone Number" } = {}) => {
    if (!digitsOf(value)) return required ? requiredMessage || `${label} is required` : ""
    return parsePhone(value)
        ? ""
        : "Please enter a valid phone number, including your country code if you are outside the US (e.g. +44 20 7946 0000)"
}

// What those forms send: US numbers in the "(619) 555-0123" format the masked
// field always produced, international ones as E.164 (+442079460000), and
// anything unparseable (only possible on optional fields) as typed
export const formatPhone = value => {
    const parsed = parsePhone(value)
    if (!parsed) return String(value || "").trim()
    if (!parsed.isUs) return parsed.e164
    const d = parsed.e164.slice(2)
    return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`
}
